import React, { useEffect, useMemo, useState } from "react";
import FormSection from "../components/FormSection";
import { listAllUnits } from "../services/unitsClient";
import scheduleRows from "../data/productionDailySchedule.json";
import aria169Rows from "../data/productionDailyScheduleAria169.json";
import edes120Rows from "../data/productionDailyScheduleEdes120.json";
import { fetchChecklistCatalog, fetchChecklistRun, fetchHolidaysByProject, saveChecklistRun } from "../services/dailyChecklistClient";
import { useAuth } from "../auth/AuthProvider";
import { normalizeUnit } from "../utils/normalizeUnit";
import { ARIA_WORKDAY_RULES } from "../data/ariaWorkdays";
import {
  DEFAULT_TOTAL_WORKDAYS,
  getChecklistCatalogId,
  getChecklistTotalWorkdays,
  isAriaFourStory,
  isAriaProject,
  normalizeChecklistProjectId,
  resolveChecklistRows,
} from "../utils/checklistSchedule";
import "../styles/utilities.css";

function getProjectKey(value) {
  return normalizeChecklistProjectId(value);
}

function isProjectedCloseTask(row) {
  const label = String(row?.operation ?? "").trim().toLowerCase();
  if (label === "projected close date") return true;
  const taskId = String(row?.task ?? row?.task_id ?? "");
  return taskId === "116";
}

function isHiddenChecklistOperation(row, buildingNumber, projectId) {
  const operation = String(row?.operation ?? "").trim().toLowerCase();
  if (!isAriaProject(projectId)) return false;
  if (isAriaFourStory(buildingNumber, projectId)) return false;
  return operation === "plumb & line floor 4";
}

function getSharedChecklistCutoff(buildingNumber, projectId) {
  return isAriaFourStory(buildingNumber, projectId) ? 55 : 46;
}

function isSharedChecklistTask(taskId, buildingNumber, projectId) {
  const parsed = Number.parseFloat(String(taskId ?? "").trim());
  if (!Number.isFinite(parsed)) return false;
  return parsed < getSharedChecklistCutoff(buildingNumber, projectId);
}

function mergeSharedCompletedDates(baseCompletedDates, sharedCompletedDates, sharedTaskIds) {
  const next = { ...(baseCompletedDates || {}) };
  (sharedTaskIds || []).forEach(taskId => {
    delete next[taskId];
  });
  Object.entries(sharedCompletedDates || {}).forEach(([taskId, date]) => {
    if (date) next[taskId] = date;
  });
  return next;
}

// Helper: UTC-based ISO string
function toISO(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Helper: Add workdays using UTC to avoid timezone shifts
function addWorkdays(startISO, days, holidays = new Set()) {
  if (!startISO || Number(days) === 0) return startISO || "";
  // Force UTC midnight
  let d = new Date(startISO + "T00:00:00Z");
  let remaining = Number(days);
  const forward = remaining >= 0;
  if (!forward) remaining = Math.abs(remaining);

  let safety = 0;
  while (remaining > 0 && safety < 2000) {
    safety++;
    // Add/Sub 1 day
    d.setUTCDate(d.getUTCDate() + (forward ? 1 : -1));

    const dayOfWeek = d.getUTCDay(); // 0=Sun, 6=Sat
    const iso = toISO(d);
    const isHoliday = holidays.has(iso);

    // If not weekend and not holiday, decrement remaining workdays
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      if (isHoliday) {
        if (safety < 100) console.log(`[Calc] Skipping Holiday: ${iso}`); // Log first few skips
      } else {
        remaining -= 1;
      }
    }
  }
  return toISO(d);
}

function formatTaskNumber(value) {
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed)) return value ? String(value) : "";
  if (Number.isInteger(parsed)) return String(parsed);
  return parsed.toFixed(2);
}

function compareNumericStrings(a, b) {
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function taskSortKey(value) {
  const raw = value == null ? "" : String(value).trim();
  if (!raw) return { isNumeric: false, number: null, text: "" };
  const cleaned = raw.replace(/,/g, "");
  const parsed = Number.parseFloat(cleaned);
  if (!Number.isNaN(parsed)) {
    return { isNumeric: true, number: parsed, text: raw };
  }
  return { isNumeric: false, number: null, text: raw };
}

function compareTaskIds(aId, bId) {
  const aKey = taskSortKey(aId);
  const bKey = taskSortKey(bId);

  if (aKey.isNumeric && bKey.isNumeric && aKey.number !== bKey.number) {
    return aKey.number - bKey.number;
  }
  if (aKey.isNumeric !== bKey.isNumeric) {
    return aKey.isNumeric ? -1 : 1;
  }
  return compareNumericStrings(aKey.text, bKey.text);
}

function taskOrderValue(row) {
  if (!row) return null;
  const direct = row.task_order ?? row.taskOrder;
  if (direct !== undefined && direct !== null && direct !== "") {
    const parsed = Number.parseFloat(String(direct).trim());
    if (!Number.isNaN(parsed)) return parsed;
  }
  const fallback = row.task ?? row.task_id ?? "";
  const parsed = Number.parseFloat(String(fallback).trim());
  return Number.isNaN(parsed) ? null : parsed;
}

export default function DailyChecklist() {
  const { user } = useAuth();
  const [region, setRegion] = useState("");
  const [projectId, setProjectId] = useState("");
  const [units, setUnits] = useState([]);
  const [allUnits, setAllUnits] = useState([]);
  const [homesite, setHomesite] = useState("");
  const [sequence, setSequence] = useState("");
  const [buildingNumber, setBuildingNumber] = useState("");
  const [projectedCloseDate, setProjectedCloseDate] = useState("");
  const [supervisor, setSupervisor] = useState("");
  const [assistant, setAssistant] = useState("");
  const [projectAccountant, setProjectAccountant] = useState("");
  const [purchaser, setPurchaser] = useState("");
  const [catalogId, setCatalogId] = useState("hbfa_daily_checklist_catalog_dev");
  const [catalogRows, setCatalogRows] = useState(() => (
    [...scheduleRows].sort((a, b) => compareTaskIds(a.task ?? a.task_id ?? "", b.task ?? b.task_id ?? ""))
  ));
  const [runDate, setRunDate] = useState(toISO(new Date()));
  const [overrides, setOverrides] = useState({});
  const [weatherDelays, setWeatherDelays] = useState({});
  const [notes, setNotes] = useState({});
  const [completedDates, setCompletedDates] = useState({});
  const [holidayDates, setHolidayDates] = useState([]);
  const [loadingRun, setLoadingRun] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");

  const regionOptions = useMemo(() => {
    const map = new Map();
    allUnits.forEach(unit => {
      const regionKey = unit.region || "Unknown";
      if (!map.has(regionKey)) {
        map.set(regionKey, { region: regionKey, state: unit.state || "" });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.region.localeCompare(b.region));
  }, [allUnits]);

  const regionMeta = useMemo(
    () => regionOptions.find(item => item.region === region) || regionOptions[0],
    [regionOptions, region]
  );

  const ariaScheduleLabel = useMemo(() => {
    return ARIA_WORKDAY_RULES
      .map(rule => `Bldg ${rule.buildings.join("/")} = ${rule.workdays}`)
      .join(", ");
  }, []);

  const projectsForRegion = useMemo(() => {
    if (!regionMeta?.region) return [];
    const projects = new Set();
    allUnits.forEach(unit => {
      const unitRegion = unit.region || "Unknown";
      if (unitRegion === regionMeta.region && unit.project_id) {
        projects.add(getProjectKey(unit.project_id));
      }
    });
    return Array.from(projects).sort();
  }, [allUnits, regionMeta]);

  useEffect(() => {
    if (!regionOptions.length) return;
    if (!region || !regionOptions.find(item => item.region === region)) {
      setRegion(regionOptions[0].region);
    }
  }, [regionOptions, region]);

  useEffect(() => {
    if (!projectsForRegion.length) return;
    if (!projectId || !projectsForRegion.includes(projectId)) {
      setProjectId(projectsForRegion[0] || "");
    }
  }, [projectsForRegion, projectId]);

  useEffect(() => {
    let abort = false;
    async function runAll() {
      try {
        const rsp = await listAllUnits();
        const items = (rsp.items || rsp || []).map(item => normalizeUnit(item || {}));
        if (!abort) setAllUnits(items);
      } catch (e) {
        console.warn("all units load error", e);
        if (!abort) setAllUnits([]);
      }
    }
    runAll();
    return () => { abort = true; };
  }, []);

  useEffect(() => {
    const projectKey = getProjectKey(projectId);
    const regionKey = regionMeta?.region;
    if (!projectKey) {
      setUnits([]);
      return;
    }
    const filtered = allUnits.filter(unit => {
      const unitProject = getProjectKey(unit.project_id);
      if (unitProject !== projectKey) return false;
      const unitRegion = unit.region || "Unknown";
      if (regionKey && unitRegion !== regionKey) return false;
      return true;
    });
    filtered.sort((a, b) => compareNumericStrings(a.unit_number ?? "", b.unit_number ?? ""));
    setUnits(filtered);
  }, [allUnits, projectId, regionMeta?.region]);

  useEffect(() => {
    const targetCatalog = getChecklistCatalogId({ projectId, buildingNumber });

    if (catalogId !== targetCatalog) {
      setCatalogId(targetCatalog);
    }
  }, [buildingNumber, projectId, catalogId]);

  useEffect(() => {
    let abort = false;
    async function run() {
      try {
        const res = await fetchChecklistCatalog({ catalog_id: catalogId });
        if (abort) return;

        const sourceRows = resolveChecklistRows({
          projectId,
          buildingNumber,
          catalogRows: res.items,
          defaultRows: scheduleRows,
          aria169Rows,
          edes120Rows,
        });
        const sorted = [...sourceRows].sort((a, b) => {
          const aOrder = taskOrderValue(a);
          const bOrder = taskOrderValue(b);
          if (aOrder !== null && bOrder !== null && aOrder !== bOrder) return aOrder - bOrder;
          if (aOrder !== null && bOrder === null) return -1;
          if (aOrder === null && bOrder !== null) return 1;
          const taskOrder = compareTaskIds(a.task ?? a.task_id ?? "", b.task ?? b.task_id ?? "");
          if (taskOrder !== 0) return taskOrder;
          return compareNumericStrings(a.operation ?? "", b.operation ?? "");
        });
        setCatalogRows(sorted);
      } catch (err) {
        console.warn("catalog load error", err);
        if (!abort) {
          setCatalogRows(resolveChecklistRows({
            projectId,
            buildingNumber,
            catalogRows: [],
            defaultRows: scheduleRows,
            aria169Rows,
            edes120Rows,
          }));
        }
      }
    }
    run();
    return () => { abort = true; };
  }, [catalogId, buildingNumber, projectId]);

  const holidaysSet = useMemo(() => new Set(holidayDates || []), [holidayDates]);

  const buildingOptions = useMemo(() => {
    const seen = new Set();
    const values = [];
    units.forEach(unit => {
      const buildingId = String(unit.building_id ?? "").trim();
      if (!buildingId || seen.has(buildingId)) return;
      seen.add(buildingId);
      values.push(buildingId);
    });
    return values.sort(compareNumericStrings);
  }, [units]);

  const unitsForBuilding = useMemo(() => {
    if (!buildingNumber) return [];
    return units
      .filter(unit => String(unit.building_id ?? "").trim() === String(buildingNumber).trim())
      .sort((a, b) => compareNumericStrings(a.unit_number ?? "", b.unit_number ?? ""));
  }, [units, buildingNumber]);

  const selectedUnit = useMemo(
    () => unitsForBuilding.find(unit => String(unit.unit_number ?? "") === String(homesite ?? "")),
    [unitsForBuilding, homesite]
  );

  const totalWorkdays = useMemo(() => {
    return getChecklistTotalWorkdays({
      projectId,
      buildingNumber,
      uniqueness: selectedUnit?.uniqueness,
    });
  }, [selectedUnit, buildingNumber, projectId]);

  const buildingUnits = useMemo(
    () => unitsForBuilding.filter(unit => unit.unit_number),
    [unitsForBuilding]
  );

  const homesiteOptions = useMemo(
    () => unitsForBuilding
      .map(u => ({ value: u.unit_number, building_id: u.building_id, sequence: u.sequence }))
      .filter(u => u.value)
      .sort((a, b) => compareNumericStrings(a.value, b.value)),
    [unitsForBuilding]
  );

  useEffect(() => {
    if (buildingOptions.length === 0) {
      setBuildingNumber("");
      setHomesite("");
      setSequence("");
      return;
    }
    if (!buildingNumber || !buildingOptions.includes(buildingNumber)) {
      setBuildingNumber(buildingOptions[0]);
    }
  }, [buildingOptions, buildingNumber]);

  useEffect(() => {
    if (homesiteOptions.length === 0) {
      setHomesite("");
      setSequence("");
      return;
    }
    const exists = homesiteOptions.find(u => u.value === homesite);
    if (!exists) {
      const next = homesiteOptions[0];
      setHomesite(next.value);
    }
  }, [homesiteOptions, homesite]);

  useEffect(() => {
    if (!selectedUnit) {
      setSequence("");
      return;
    }
    const nextSequence = selectedUnit.sequence ?? "";
    setSequence(nextSequence !== "" && nextSequence !== undefined ? String(nextSequence) : "");
  }, [selectedUnit]);

  useEffect(() => {
    let abort = false;
    async function run() {
      if (!projectId) {
        if (!abort) setHolidayDates([]);
        return;
      }
      try {
        const res = await fetchHolidaysByProject({ project_id: projectId, region });
        const items = res.items || [];
        const dates = items.map(item => item.date).filter(Boolean);
        if (!abort) setHolidayDates(dates);
      } catch (err) {
        if (!abort) setHolidayDates([]);
      }
    }
    run();
    return () => { abort = true; };
  }, [projectId]);

  const sortedCatalogRows = useMemo(() => {
    return [...catalogRows].sort((a, b) => {
      const aOrder = taskOrderValue(a);
      const bOrder = taskOrderValue(b);
      if (aOrder !== null && bOrder !== null && aOrder !== bOrder) return aOrder - bOrder;
      if (aOrder !== null && bOrder === null) return -1;
      if (aOrder === null && bOrder !== null) return 1;
      const aId = a.task ?? a.task_id ?? "";
      const bId = b.task ?? b.task_id ?? "";
      const taskOrder = compareTaskIds(aId, bId);
      if (taskOrder !== 0) return taskOrder;
      return compareNumericStrings(a.operation ?? "", b.operation ?? "");
    });
  }, [catalogRows]);

  const visibleCatalogRows = useMemo(() => {
    return sortedCatalogRows.filter(row => !isHiddenChecklistOperation(row, buildingNumber, projectId));
  }, [sortedCatalogRows, buildingNumber, projectId]);

  const sharedTaskIds = useMemo(() => {
    return visibleCatalogRows
      .map(row => String(row.task ?? row.task_id ?? "").trim())
      .filter(taskId => taskId && isSharedChecklistTask(taskId, buildingNumber, projectId));
  }, [visibleCatalogRows, buildingNumber, projectId]);

  useEffect(() => {
    let abort = false;
    async function run() {
      if (!projectId || !homesite || !runDate) return;
      setLoadingRun(true);
      try {
        const resolved = await fetchChecklistRun({
          project_id: projectId,
          unit_number: homesite,
          run_date: runDate,
        });
        if (abort) return;

        if (resolved?._loaded_from_latest && resolved.run_date && resolved.run_date !== runDate) {
          setRunDate(resolved.run_date);
        }

        let mergedCompleted = resolved?.completed_dates || {};
        if (sharedTaskIds.length > 0 && buildingUnits.length > 1) {
          const siblingRuns = await Promise.all(
            buildingUnits
              .filter(unit => String(unit.unit_number ?? "") !== String(homesite))
              .map(async unit => {
                try {
                  return await fetchChecklistRun({
                    project_id: projectId,
                    unit_number: unit.unit_number,
                    run_date: runDate,
                  });
                } catch (error) {
                  console.warn("shared checklist sibling load error", unit.unit_number, error);
                  return {};
                }
              })
          );
          if (abort) return;

          const sharedCompleted = {};
          [resolved, ...siblingRuns].forEach(runItem => {
            Object.entries(runItem?.completed_dates || {}).forEach(([taskId, date]) => {
              if (date && sharedTaskIds.includes(String(taskId))) {
                sharedCompleted[String(taskId)] = date;
              }
            });
          });
          mergedCompleted = mergeSharedCompletedDates(mergedCompleted, sharedCompleted, sharedTaskIds);
        }

        setOverrides(resolved?.overrides || {});
        setWeatherDelays(resolved?.weather_delays || {});
        setNotes(resolved?.notes || {});
        setCompletedDates(mergedCompleted);
        setSupervisor(resolved?.supervisor || "");
        setAssistant(resolved?.assistant || "");
        setProjectAccountant(resolved?.project_accountant || "");
        setPurchaser(resolved?.purchaser || "");
        setProjectedCloseDate(resolved?.projected_close_date || "");
      } catch (err) {
        if (!abort) {
          setOverrides({});
          setWeatherDelays({});
          setNotes({});
          setCompletedDates({});
          setSupervisor("");
          setAssistant("");
          setProjectAccountant("");
          setPurchaser("");
          setProjectedCloseDate("");
        }
      } finally {
        if (!abort) setLoadingRun(false);
      }
    }
    run();
    return () => { abort = true; };
  }, [projectId, homesite, runDate, sharedTaskIds, buildingUnits]);

  const completionDates = useMemo(() => {
    if (!projectedCloseDate) return {};
    const map = {};

    // Base = Finish - TotalWorkdays (varies by building/project rules).
    // The component calculates `totalWorkdays` and includes it as a dependency.

    // Safety check: if totalWorkdays is missing, fallback to 156 (DEFAULT)
    const effectiveTotal = totalWorkdays || DEFAULT_TOTAL_WORKDAYS;

    console.log(`[Calc] Finish: ${projectedCloseDate}, Using Component TotalWorkdays: ${effectiveTotal}`);

    // Calculate Base Start Date
    const base = addWorkdays(projectedCloseDate, -effectiveTotal, holidaysSet);
    console.log(`[Calc] Base Start Date calculated: ${base}`);

    visibleCatalogRows.forEach((row) => {
      const taskId = row.task ?? row.task_id ?? "";
      const overrideValue = Number(overrides[taskId]) || 0;
      const dayOffset = (Number(row.offset) || 0) + overrideValue;

      if (base) {
        // Force Projected Close row to match the close date exactly
        if (isProjectedCloseTask(row) && overrideValue === 0) {
          map[taskId] = projectedCloseDate;
        } else {
          const finalDate = addWorkdays(base, dayOffset, holidaysSet);
          map[taskId] = finalDate;
        }

        if (taskId === "1" || taskId === "116") {
          console.log(`[Calc] Task ${taskId}: Base(${base}) + Offset(${dayOffset}) = ${map[taskId]}`);
        }
      }
    });
    return map;
  }, [projectedCloseDate, overrides, visibleCatalogRows, holidaysSet, totalWorkdays]);

  function validateOverride(task, value, isWeather) {
    if (!user) return true; // Fallback only
    if (user.role === 'ops_admin' || user.role === 'ops_vp' || user.role === 'ops_sales_vp') return true;
    if (user.role === 'ops_field') {
      const val = Number(value);
      if (Number.isNaN(val)) return true; // Allow clearing

      const maxDelay = isWeather ? 5 : 1;
      const minDelay = -1;

      return val >= minDelay && val <= maxDelay;
    }
    return false; // Unknown role -> read only ideally, but here just block
  }

  function setOverride(task, value) {
    const next = value === "" ? "" : Number(value);
    const isWeather = weatherDelays[task] || false;

    // Check validation
    if (!validateOverride(task, next, isWeather)) {
      // If invalid, just don't update state - input remains at previous value
      return;
    }

    setOverrides(prev => ({ ...prev, [task]: Number.isNaN(next) ? "" : next }));
  }

  function toggleWeatherDelay(task, checked) {
    setWeatherDelays(prev => ({ ...prev, [task]: checked }));

    // If unchecking weather delay, we might need to reset override if it is now out of bounds
    // But requirement only says "max delay is upto ... five days only" if checked.
    // If unchecked, and value is 5, it is now invalid.
    // However, usually we validate on input. If we want to strictly enforce, we should clamp it here.
    if (!checked) {
      const currentOverride = overrides[task] ? Number(overrides[task]) : 0;
      if (currentOverride > 1) {
        // If the user is a field user, clamp it down to 1?
        // Or just leave it and let them fix it? 
        // "Validation in the override box is one work day ... unless Weather Delay is checked"
        // I'll auto-clamp for field users to avoid invalid state
        if (user?.role === 'ops_field') {
          setOverrides(prev => ({ ...prev, [task]: 1 }));
        }
      }
    }
  }

  function setNote(task, value) {
    setNotes(prev => ({ ...prev, [task]: value.slice(0, 256) }));
  }

  function toggleComplete(task, checked) {
    if (!checked) {
      setCompletedDates(prev => {
        const next = { ...prev };
        delete next[task];
        return next;
      });
      return;
    }
    const today = toISO(new Date());
    setCompletedDates(prev => ({ ...prev, [task]: today }));
  }

  async function handleSave() {
    if (!projectId || !homesite || !runDate) return;
    setSaveStatus("Saving...");
    try {
      const sharedCompletedDates = Object.fromEntries(
        sharedTaskIds
          .filter(taskId => completedDates[taskId])
          .map(taskId => [taskId, completedDates[taskId]])
      );

      const selectedPayload = {
        project_id: projectId,
        unit_number: homesite,
        run_date: runDate,
        catalog_id: catalogId,
        sequence,
        building_number: buildingNumber,
        supervisor,
        assistant,
        project_accountant: projectAccountant,
        purchaser,
        projected_close_date: projectedCloseDate,
        total_workdays: totalWorkdays,
        overrides,
        weather_delays: weatherDelays,
        notes,
        completed_dates: mergeSharedCompletedDates(completedDates, sharedCompletedDates, sharedTaskIds),
      };

      const targetUnits = buildingUnits.length > 0 ? buildingUnits : [selectedUnit].filter(Boolean);
      await Promise.all(targetUnits.map(async unit => {
        const unitNumber = String(unit?.unit_number ?? "").trim();
        if (!unitNumber) return;

        if (unitNumber === String(homesite)) {
          await saveChecklistRun(selectedPayload);
          return;
        }

        const existingRun = await fetchChecklistRun({
          project_id: projectId,
          unit_number: unitNumber,
          run_date: runDate,
        });

        await saveChecklistRun({
          project_id: projectId,
          unit_number: unitNumber,
          run_date: runDate,
          catalog_id: catalogId,
          sequence: unit?.sequence ?? existingRun?.sequence ?? "",
          building_number: buildingNumber || existingRun?.building_number || unit?.building_id || "",
          supervisor: existingRun?.supervisor || supervisor,
          assistant: existingRun?.assistant || assistant,
          project_accountant: existingRun?.project_accountant || projectAccountant,
          purchaser: existingRun?.purchaser || purchaser,
          projected_close_date: existingRun?.projected_close_date || projectedCloseDate,
          total_workdays: Number(existingRun?.total_workdays) || totalWorkdays,
          overrides: existingRun?.overrides || {},
          weather_delays: existingRun?.weather_delays || {},
          notes: existingRun?.notes || {},
          completed_dates: mergeSharedCompletedDates(
            existingRun?.completed_dates || {},
            sharedCompletedDates,
            sharedTaskIds
          ),
        });
      }));
      setSaveStatus("Saved");
    } catch (err) {
      console.error("save checklist error", err);
      setSaveStatus("Save failed");
    } finally {
      setTimeout(() => setSaveStatus(""), 2000);
    }
  }

  const saveButton = (
    <button
      onClick={handleSave}
      disabled={!projectId || !homesite || !runDate}
      className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
    >
      {saveStatus === "Saving..." ? "Saving..." : "Save Checklist"}
    </button>
  );

  const saveBreakTasks = new Set([31, 64, 97]);

  return (
    <div className="page">
      <div className="page-header">
        <h2>Daily Checklist</h2>
        <p>Field operations checklist for construction supervisors.</p>
        <div style={{ fontSize: '0.8rem', color: '#666' }}>
          Logged in as: {user?.email} ({user?.role})
          <br />
          <span style={{ color: 'red', fontWeight: 'bold' }}>Build: 2026-02-19 13:25 PST (Dynamic Fix)</span>
        </div>
      </div>

      <FormSection title="Context">
        <div className="grid-2">
          <label>
            Region
            <select value={region} onChange={e => setRegion(e.target.value)}>
              {regionOptions.map(item => (
                <option key={item.region} value={item.region}>{item.region}</option>
              ))}
            </select>
          </label>
          <label>
            State
            <input type="text" value={regionMeta?.state || ""} readOnly />
          </label>
          <label>
            Project
            <select value={projectId} onChange={e => setProjectId(e.target.value)}>
              {projectsForRegion.map(project => (
                <option key={project} value={project}>{project}</option>
              ))}
            </select>
          </label>
          <label>
            Building Number
            <select value={buildingNumber} onChange={e => setBuildingNumber(e.target.value)}>
              <option value="">-- Select --</option>
              {buildingOptions.map(item => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </label>
          <label>
            Homesite
            <select value={homesite} onChange={e => setHomesite(e.target.value)} disabled={!buildingNumber}>
              <option value="">-- Select --</option>
              {homesiteOptions.map(item => (
                <option key={item.value} value={item.value}>{item.value}</option>
              ))}
            </select>
          </label>
          <label>
            Sequence
            <input type="text" value={sequence} readOnly />
          </label>
        </div>
      </FormSection>

      <FormSection title="Contacts">
        <div className="grid-2">
          <label>
            Supervisor
            <input value={supervisor} onChange={e => setSupervisor(e.target.value)} />
          </label>
          <label>
            Assistant
            <input value={assistant} onChange={e => setAssistant(e.target.value)} />
          </label>
          <label>
            Project Accountant
            <input value={projectAccountant} onChange={e => setProjectAccountant(e.target.value)} />
          </label>
          <label>
            Purchaser
            <input value={purchaser} onChange={e => setPurchaser(e.target.value)} />
          </label>
        </div>
      </FormSection>

      <FormSection title="Schedule">
        <div className="grid-2">
          <label>
            Run Date
            <input type="date" value={runDate} onChange={e => setRunDate(e.target.value)} />
          </label>
          <label>
            Projected Close Date
            <input type="date" value={projectedCloseDate} onChange={e => setProjectedCloseDate(e.target.value)} />
          </label>
          <label>
            Total Workdays
            <input type="number" value={totalWorkdays} readOnly />
          </label>
        </div>
        {getProjectKey(projectId) === "Aria" && (
          <div className="banner info" style={{ marginTop: 8 }}>
            Aria workday schedule (hardcoded): {ariaScheduleLabel}.
          </div>
        )}
        {loadingRun && (
          <div className="banner info" style={{ marginTop: 8 }}>
            Loading checklist...
          </div>
        )}
        {buildingNumber && (
          <div className="banner info" style={{ marginTop: 8 }}>
            Tasks before Plumbing Vents save as shared completions for every unit in building {buildingNumber}. Plumbing Vents and later remain unit-specific.
          </div>
        )}
        <div className="flex justify-end" style={{ marginTop: 8 }}>
          {saveButton}
        </div>
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="table">
            <colgroup>
              <col style={{ width: 50 }} />
              <col />
              <col style={{ width: 120 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 220 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Done</th>
                <th>Operation</th>
                <th>Completion Dates</th>
                <th>Completed On</th>
                <th>Weather</th>
                <th>Override</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {visibleCatalogRows.map((row, idx) => {
                const taskId = row.task ?? row.task_id ?? "";
                const taskNumber = Number.parseFloat(String(taskId));
                const showSaveBreak = Number.isFinite(taskNumber) && saveBreakTasks.has(taskNumber);
                return (
                  <React.Fragment key={`${taskId}-${row.operation}`}>
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          checked={Boolean(completedDates[taskId])}
                          onChange={e => toggleComplete(taskId, e.target.checked)}
                        />
                      </td>
                      <td className="text-strong">
                        {row.operation}
                        {isSharedChecklistTask(taskId, buildingNumber, projectId) ? " (Shared)" : ""}
                      </td>
                      <td>{completionDates[taskId] || ""}</td>
                      <td>{completedDates[taskId] || ""}</td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={Boolean(weatherDelays[taskId])}
                          onChange={e => toggleWeatherDelay(taskId, e.target.checked)}
                        />
                      </td>
                      <td style={{ minWidth: 80 }}>
                        {/* Only show override input if role allows? Or show disabled? 
                          User: "_admin can make undefined additional changes, _vp can change OFFSET and OVERRIDE with no restrictions, _field has the right of override as stated above."
                          So all can edit override, but validation differs.
                      */}
                        <input
                          type="number"
                          value={overrides[taskId] ?? 0}
                          onChange={e => setOverride(taskId, e.target.value)}
                          inputMode="numeric"
                          style={{ width: "100%" }}
                        />
                      </td>
                      <td style={{ minWidth: 260 }}>
                        <textarea
                          rows={2}
                          value={notes[taskId] || ""}
                          onChange={e => setNote(taskId, e.target.value)}
                          placeholder="Notes (256 chars max)"
                        />
                      </td>
                    </tr>
                    {showSaveBreak && (
                      <tr className="save-break-row">
                        <td colSpan={7}>
                          <div className="flex justify-end">
                            {saveButton}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </FormSection>
      <div className="flex justify-end mt-8">
        {saveButton}
      </div>

      {/* DEBUG OVERLAY */}
      <div className="mt-8 p-4 bg-gray-100 border border-gray-300 rounded text-xs font-mono">
        <h3 className="font-bold border-b mb-2">Debug Info (v6-Render-Fix)</h3>
        <p>Holidays Loaded: {holidayDates.length}</p>
        <p>Projected Close: {projectedCloseDate || "(none)"}</p>
        <p>Catalog ID: {catalogId}</p>
        <p>Total Workdays: {totalWorkdays}</p>
        {/* Calculate Base Date for display */}
        <p>Base Date (Calc): {completionDates["1"] ? addWorkdays(completionDates["1"], -((Number(visibleCatalogRows.find(r => (r.task === "1" || r.task_id === "1"))?.offset) || 0)), holidaysSet) : "Waiting..."}</p>
        <div className="mt-2 h-24 overflow-y-auto bg-white p-2 border">
          {holidayDates.length > 0 ? holidayDates.slice(0, 10).join(", ") + "..." : "No holidays loaded"}
        </div>
      </div>
    </div>
  );
}
